import React from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useTheme } from '@/context/ThemeContext';
import { AccountCard, CardColumn, ScopeChip } from '@/pages/account/primitives';
import {
  COLOR_THEMES,
  DISPLAY_MODES,
  TEXT_SIZES,
  type ColorTheme,
  type DisplayMode,
  type TextSize,
} from '../../../../shared/platform-settings';
import { SettingsPageShell } from './SettingsPageShell';
import { SETTINGS_CARDS } from './settingsSearchIndex';

/**
 * AR-390 — Color Theme, the Settings module's home page.
 *
 * D-ACCT-27 (Jason, Surendra, Britta) — the color theme, the display mode and
 * the text size are universal platform settings rather than AIVIA preferences,
 * so the Appearance card moved here whole out of Preferences + AIVIA. There is
 * no Edit and no Save — each control takes effect on the click.
 *
 * AR-391 — the three controls read and write the MEMBER RECORD
 * (`users.color_theme / display_mode / text_size`, AR-393) through
 * ThemeContext, the one writer of the appearance: the same setter the user
 * menu's Dark mode row lands on, so two controls cannot disagree. They follow
 * the member's login, not the device (Britta, 2026-09-21) — a choice made
 * here shows on every device they sign in to, and nothing on this page reads
 * localStorage. With the module's flag off the page is not shown at all.
 *
 * `system` is a stored choice in its own right ("Use device setting",
 * D-ACCT-15): the segment shows the CHOICE, and ThemeContext resolves it
 * against the device at runtime.
 */

/**
 * The three swatches each theme's picker tile shows: accent, deep, pale. The
 * ids and labels are the shared allow-list's (`shared/platform-settings.ts`),
 * so a theme cannot exist on this page and be refused by the server.
 */
const THEME_SWATCHES: Record<ColorTheme, readonly [string, string, string]> = {
  clairvoyant: ['#4b0a5b', '#3d1751', '#ecdcf1'],
  coral: ['#ff7540', '#9e3e19', '#ffedd5'],
  hibiscus: ['#b82f74', '#4b0a5b', '#f8dcea'],
  teal: ['#008588', '#02d8b2', '#d2f0ec'],
  govbay: ['#2b38b6', '#1d2782', '#dfe2f8'],
};

/** A segmented control — the prototype's `.seg`. */
const Segment = <T extends string>({
  label,
  options,
  value,
  onChange,
  last,
}: {
  label: string;
  options: readonly { readonly id: T; readonly label: string }[];
  value: T;
  onChange: (next: T) => void;
  last?: boolean;
}) => (
  <div
    className={cn(
      'flex flex-wrap items-center gap-2.5 py-2.5',
      !last && 'border-b border-gray-100 dark:border-gray-800',
    )}
  >
    <span className="min-w-0 flex-1 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
      {label}
    </span>
    <span
      role="group"
      aria-label={label}
      className="inline-flex flex-none overflow-hidden rounded-md border border-gray-200 dark:border-gray-700"
    >
      {options.map(({ id, label: text }) => {
        const on = value === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(id)}
            className={cn(
              'px-3 py-1 text-[13.5px] font-medium transition-colors',
              'border-r border-gray-200 last:border-r-0 dark:border-gray-700',
              on
                ? 'bg-orange-700 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
            )}
          >
            {text}
          </button>
        );
      })}
    </span>
  </div>
);

export const SettingsColorTheme: React.FC = () => {
  const { colorTheme, setColorTheme, displayMode, setDisplayMode, textSize, setTextSize } =
    useTheme();

  return (
    <SettingsPageShell page="theme">
      <CardColumn>
        <AccountCard name={SETTINGS_CARDS.appearance.name} chip={<ScopeChip scope="personal" />}>
          <div className="border-b border-gray-100 py-2.5 dark:border-gray-800">
            <b className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
              Color theme
            </b>
            <div
              role="group"
              aria-label="Color theme"
              className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
            >
              {COLOR_THEMES.map((t) => {
                const on = colorTheme === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setColorTheme(t.id)}
                    data-settings-theme={t.id}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
                      on
                        ? 'border-orange-400 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/20'
                        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800',
                    )}
                  >
                    <span aria-hidden className="flex flex-none -space-x-1">
                      {THEME_SWATCHES[t.id].map((d) => (
                        <span
                          key={d}
                          style={{ background: d }}
                          className="h-4 w-4 rounded-full ring-1 ring-white dark:ring-gray-900"
                        />
                      ))}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-gray-900 dark:text-gray-100">
                      {t.label}
                    </span>
                    {on ? (
                      <Check
                        aria-hidden
                        className="h-4 w-4 flex-none text-orange-700 dark:text-orange-400"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <Segment<DisplayMode>
            label="Display mode"
            options={DISPLAY_MODES}
            value={displayMode}
            onChange={setDisplayMode}
          />

          {/* D-ACCT-24 (Britta) — the whole app scales, not the text alone, so
              nothing overlaps and no line runs out of its box. */}
          <Segment<TextSize>
            label="Text size"
            options={TEXT_SIZES}
            value={textSize}
            onChange={setTextSize}
            last
          />
        </AccountCard>
      </CardColumn>
    </SettingsPageShell>
  );
};

export default SettingsColorTheme;
